import React, { useEffect, useRef, useState } from 'react'
import WaveSurfer from 'wavesurfer.js'
import styled from 'styled-components'
import Play from '../../../../assets/play.svg'
import Pause from '../../../../assets/pause.png'
import Voice from '../../../../assets/volume.svg'
import { InfoTooltip } from 'ui-kit/Tooltip/InfoTooltip'
import TimeInput from './TimeInput'
import Muted from '../../../../assets/muted.png'
import { isPerfDebugEnabled, perfLog, perfNow } from "utils/perfDebug";

interface WaveformPlayerProps {
  fileUrl: string
  fileName: string
  setAudioFiles: any
  startTime: string
  setStartTime: (value: string) => void
  onDelete?: () => void
}


const WaveformPlayer: React.FC<WaveformPlayerProps> = ({
  fileUrl,
  fileName,
  setAudioFiles,
  setStartTime,
  startTime,
  onDelete,
}) => {
  const waveformRef = useRef<HTMLDivElement | null>(null)
  const waveSurferRef = useRef<WaveSurfer | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1) 
  const [isMuted, setIsMuted] = useState(false);


  useEffect(() => {
    if (!waveformRef.current) return
    const perf = isPerfDebugEnabled();
    const t0 = perf ? perfNow() : 0;
    if (perf) {
      perfLog("NewRelease.WaveSurfer.create", { fileUrlPrefix: String(fileUrl).slice(0, 40) });
    }
    waveSurferRef.current = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: '#3F478B',
      progressColor: '#6777FF',
      height: 130,
      barWidth: 2,
    })

    if (perf) perfLog("NewRelease.WaveSurfer.load.start", {});
    waveSurferRef.current.load(fileUrl)
    waveSurferRef.current.on('ready', () => {
      waveSurferRef.current?.setVolume(volume)
      setDuration(waveSurferRef.current?.getDuration() || 0)
      const startSeconds = Math.max(1, Number.parseInt(String(startTime || "1"), 10) || 1);
      waveSurferRef.current?.setTime(startSeconds)
      setCurrentTime(startSeconds)
      if (perf) {
        perfLog("NewRelease.WaveSurfer.ready", {
          ms: Math.round(perfNow() - t0),
          durationSec: waveSurferRef.current?.getDuration?.(),
        });
      }
    })

    waveSurferRef.current.on('audioprocess', () => {
      setCurrentTime(waveSurferRef.current?.getCurrentTime() || 0)
    })

    waveSurferRef.current.on('finish', () => {
      setIsPlaying(false)
      setCurrentTime(0)
    })

    return () => {
      waveSurferRef.current?.destroy()
    }
  }, [fileUrl])

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value)
    setVolume(newVolume)
    waveSurferRef.current?.setVolume(newVolume)
    setVolume(parseFloat(e.target.value));
    if (isMuted && parseFloat(e.target.value) > 0) {
      setIsMuted(false);
    }
  }



  const togglePlay = () => {
    if (!waveSurferRef.current) return
    waveSurferRef.current.playPause()
    setIsPlaying((prev) => !prev)
  }

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`
  }
  const toggleMute = () => {
    setIsMuted(prev => !prev);
  };
  useEffect(() => {
    if (waveSurferRef.current) {
      waveSurferRef.current.setVolume(isMuted ? 0 : volume);
    }
  }, [volume, isMuted]);
  
  return (
    <Container>
      <FileName>
        <span>{fileName}</span>{' '}
        <Delete
          onClick={() => {
            setAudioFiles([]);
            onDelete?.();
          }}
        >
          Delete
        </Delete>
      </FileName>
      <div ref={waveformRef} />
      <Box>
        <PlayBox>
          <PlayButton onClick={togglePlay}>
            {isPlaying ? (
              <img src={Pause} alt="pause" width={20} height={20} />
            ) : (
              <Play />
            )}
          </PlayButton>
          <TimeDisplay>
            {formatTime(currentTime)} / {formatTime(duration)}
          </TimeDisplay>
        </PlayBox>

        <VolumeBox>
          {!isMuted && volume !== 0 ? <div onClick={toggleMute} style={{ cursor: 'pointer', display: 'inline-block' }}>
            <ColoredVoice width="24" height="24" color={isMuted ? '#888888' : '#6777FF'} />
          </div> :
            <div onClick={toggleMute} style={{width:"24", height:"24"}}><img src={Muted} width={24} height={24}/></div>}
          <VolumeSlider
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            style={{
              background: isMuted
                ? `linear-gradient(to right, #888888 0%, #888888 100%)`
                : `linear-gradient(to right, #6777FF 0%, #6777FF ${volume * 100}%, #cccccc ${volume * 100}%, #cccccc 100%)`,
              borderRadius: '4px',
              height: '6px',
              appearance: 'none',
            }}
          />

        </VolumeBox>
      </Box>
      <TrackContent>
        <Title>
          <span>Start of playback</span>{' '}
          <InfoTooltip name="platform-info" tooltip="Tooltip" />
        </Title>
        <TimerBox>
          <TimeInput setStartTime={setStartTime} startTime={startTime} />
        </TimerBox>
      </TrackContent>
    </Container>
  )
}

// styled
const Container = styled.div`
  margin-top: 23px;
  border-radius: 8px;
`

const FileName = styled.span`
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const PlayButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`

const TimeDisplay = styled.div`
  font-size: 14px;
  color: #444;
`

const Box = styled.div`
  width: 100%;
  height: 41px;
  border-radius: 10px;
  background-color: #e9e9e9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 0 10px;
`

const PlayBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const Delete = styled.span`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  color: #a4a4a4;
  cursor: pointer;
`

const TrackContent = styled.div``

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  color: #000000;
  padding: 25px 0 0 3px;
`
const TimerBox = styled.div`
  margin-top: 7px;
  padding: 0 14px;
  width: 100%;
  height: 43px;
  border-radius: 10px;
  border: 1px solid #e9e9e9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: SF Pro Display;
  font-size: 13px;
  color: #00000080;
`
const VolumeBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`

const ColoredVoice = styled(Voice)`
  width: 20px;
  height: 20px;
  fill: #6777FF;  
  color: red;
`

const VolumeSlider = styled.input`
  width: 100px;
  cursor: pointer;
  -webkit-appearance: none; 

  &::-webkit-slider-runnable-track {
    height: 4px;
    border-radius: 2px;
  }
  &::-moz-range-track {
    height: 4px;
    background: #000000;
    border-radius: 2px;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #6777FF;
    margin-top: -5px; /* center thumb on track */
  }
  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #6777FF;
  }
`
export default WaveformPlayer
